import React from 'react';
import useField from '../hooks/useField';
import { TDefaultFieldType } from './types';
import useValidationClass from './useValidationClass';
import Wrapper from './Wrapper';
import PrimitiveSelect, {
  TSelect as TPrimitiveSelect,
} from '../../fields/Select';
import useFieldStatesClassNames from '../hooks/useFieldStyles';

export type TSelect = TPrimitiveSelect &
  TDefaultFieldType<string> & {
    options?: { value: string; label: string }[];
  };

const Select = React.forwardRef<HTMLSelectElement, TSelect>(
  (
    {
      avoidSemicolon,
      className,
      formName,
      label,
      onChange: outerOnChange,
      options,
      /**
       * useField
       */
      avoidRegisterIfExists,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });

    const { updateValidations, onChange, isTouched, ...inputProps } =
      useField<string>({
        avoidRegisterIfExists,
        initialValue: initialValue ?? (value as string) ?? options?.[0]?.value,
        name,
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });
    const stateClassName = useFieldStatesClassNames(name);

    return (
      <Wrapper
        name={name}
        label={label}
        className={`select ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        <PrimitiveSelect
          {...window.getVariant('forms.select')}
          {...props}
          {...inputProps}
          className={stateClassName}
          onChange={React.useCallback(
            (ev: React.ChangeEvent<HTMLSelectElement>) => {
              onChange(ev);
              if (outerOnChange) {
                outerOnChange(ev);
              }
            },
            [onChange, outerOnChange],
          )}
          aria-label={props['aria-label'] ?? label}
          ref={ref}
          value={inputProps.value ?? ''}
        >
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {props.children}
        </PrimitiveSelect>
      </Wrapper>
    );
  },
);

export default React.memo(Select);
